import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateRange, DayPicker, getDefaultClassNames } from 'react-day-picker';
import { format, subMonths } from 'date-fns';
import { Mail } from 'lucide-react';
import { Filter } from '../Filter';
import { BadgeIndicator } from '../BadgeIndicator';
import { ISearchFilters } from '../../types/searchFilters';
import { ISelectedRecord } from '../../types/selectedRecord';
import { Loader } from '../Loader';
import { Pagination } from '../Pagination';
import 'react-day-picker/style.css';
import Popover from '../Popover';
import { useApiClient } from '../../services/api-client';
import { IManagementAPIPolicySearchResponse } from '../../types/management-api/policy/search/response';
import { transformSearchQueryParams } from '../../transformers/policies/request/transformSearchQueryParams';
import PolicySummary from '../Policy/Summary/Summary';
import PageHeader from '../Typography/PageHeader/PageHeader';
import Modal from '../Modal/Modal';
import Email from '../Email/Email';

interface PageProps {
  children?: React.ReactNode;
}

const defaultArgs = {
  page: 1,
  policyNumber: '',
  firstName: '',
  lastName: '',
  isActive: false,
  sortByNewestFirst: true,
};

const defaultSelectedRecordArgs = {
  email: '',
  policyId: 0,
};

const PoliciesPage: React.FC<PageProps> = () => {
  const {
    isAuthenticated,
    isLoading: auth0IsLoading,
    loginWithRedirect,
  } = useAuth0();

  const [filterData, setFilterData] = useState<ISearchFilters>(defaultArgs);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ISelectedRecord>(
    defaultSelectedRecordArgs
  );

  // START DATEPICKER
  const handleDateSelect = (selectedRange: DateRange | undefined) => {
    if (!selectedRange?.from || !selectedRange?.to) return; // Prevent clearing selection

    setFilterData({
      ...filterData,
      startDate: selectedRange.from,
      endDate: selectedRange.to,
    });
  };
  const defaultClassNames = getDefaultClassNames();
  //END DATEPICKER

  const setCurrentPage = async (page: number) => {
    setFilterData({ ...filterData, page });
    mutation.mutate();
  };

  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { isFetching, data } = useQuery<IManagementAPIPolicySearchResponse>({
    queryKey: ['policies'],
    queryFn: () =>
      apiClient.get('/policy/search', transformSearchQueryParams(defaultArgs)),
    enabled: isAuthenticated,
  });

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.get('/policy/search', transformSearchQueryParams(filterData)),
    onSuccess: data => {
      queryClient.setQueryData(['policies'], data);
    },
  });

  return (
    <div className="container mx-auto">
      <PageHeader
        mainHeaderText="Policy Navigator"
        subHeaderText="Browse or search for policies"
      />

      {import.meta.env.VITE_ENVIRONMENT === 'local' ? (
        <div className="bg-red-100 my-4 p-4">
          Filters: <pre>{JSON.stringify(filterData)}</pre>
          Page: <pre>{filterData.page}</pre>
          Fetched Meta: <pre>{JSON.stringify(data?.meta)}</pre>
        </div>
      ) : null}

      {isAuthenticated ? (
        <>
          {isFetching || mutation.isPending ? (
            <Loader />
          ) : (
            <>
              <div className="flex gap-x-4 h-9">
                <Filter
                  filterData={filterData}
                  setFilterData={setFilterData}
                  name={'policyNumber'}
                  placeholder="Policy Number"
                  classes="border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-ingle-light-purple focus:border-ingle-light-purple block px-4 outline-0"
                />

                <Filter
                  filterData={filterData}
                  setFilterData={setFilterData}
                  name={'firstName'}
                  placeholder="First Name"
                  classes="border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-ingle-light-purple focus:border-ingle-light-purple block px-4 outline-0"
                />

                <Filter
                  filterData={filterData}
                  setFilterData={setFilterData}
                  name={'lastName'}
                  placeholder="Last Name"
                  classes="border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-ingle-light-purple focus:border-ingle-light-purple block px-4 outline-0"
                />

                <Popover
                  buttonText={
                    filterData.dateOfBirth
                      ? `${format(filterData.dateOfBirth, 'yyyy-MM-dd')}`
                      : 'Date of Birth'
                  }
                  title="Select Date of Birth"
                >
                  <DayPicker
                    mode="single"
                    selected={filterData.dateOfBirth}
                    onSelect={date =>
                      setFilterData({
                        ...filterData,
                        dateOfBirth: date,
                      })
                    }
                    defaultMonth={subMonths(new Date(), 60)}
                    endMonth={subMonths(new Date(), 60)}
                    captionLayout="dropdown"
                    classNames={{
                      months_dropdown: `p-5`,
                      selected: `bg-ingle-light-purple border-0 text-white font-bold rounded`,
                      chevron: `fill-ingle-light-purple`,
                      root: `${defaultClassNames.root} p-5`,
                    }}
                  />
                </Popover>

                <Popover
                  buttonText={
                    filterData.startDate && filterData.endDate
                      ? `${format(filterData.startDate, 'yyyy-MM-dd')} → ${format(filterData.endDate, 'yyyy-MM-dd')}`
                      : 'Start Date & End Date'
                  }
                  title="Select Start Date & End Date Range"
                >
                  <DayPicker
                    mode="range"
                    selected={{
                      to: filterData.startDate,
                      from: filterData.endDate,
                    }}
                    onSelect={handleDateSelect}
                    numberOfMonths={2}
                    defaultMonth={subMonths(new Date(), 1)}
                    classNames={{
                      range_start: `bg-ingle-light-purple border-0 text-white font-bold rounded`,
                      range_end: `bg-ingle-light-purple border-0 text-white font-bold rounded`,
                      months: `gap-5 flex`,
                      selected: `bg-ingle-light-purple border-0`,
                      today: `text-ingle-dark-purple font-bold`,
                      chevron: `fill-ingle-light-purple`,
                      root: `${defaultClassNames.root} p-5`,
                    }}
                  />
                </Popover>

                <Filter
                  filterData={filterData}
                  setFilterData={setFilterData}
                  name={'isActive'}
                  placeholder="Show Active Only"
                  type="toggle"
                />

                <button
                  className="text-white bg-ingle-light-purple hover:bg-ingle-dark-purple focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 hover:cursor-pointer"
                  onClick={() => {
                    return (
                      setFilterData({ ...filterData, page: 1 }),
                      mutation.mutate()
                    );
                  }}
                >
                  Apply
                </button>

                {filterData !== defaultArgs ? (
                  <button
                    className="text-white bg-red-500 hover:bg-red-200 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 hover:cursor-pointer"
                    onClick={() => {
                      return setFilterData(defaultArgs), mutation.mutate();
                    }}
                  >
                    Clear Filters
                  </button>
                ) : null}
              </div>

              <PolicySummary
                totalActiveItems={data?.meta.totalActiveItems}
                totalInActiveItems={data?.meta.totalInActiveItems}
                totalItems={data?.meta.totalItems}
              />

              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500">
                  <thead className="text-xs text-gray-700 uppercase text-center">
                    <tr className="border-b border-gray-300">
                      <th className="px-6 py-4">Policy Number</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">First Name</th>
                      <th className="px-6 py-4">Last Name</th>
                      <th className="px-6 py-4">Date Of Birth</th>
                      <th className="px-6 py-4">Email</th>
                      <th className="px-6 py-4">Start Date</th>
                      <th className="px-6 py-4">End Date</th>
                      <th className="px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.policies.map(policy => {
                      return (
                        <tr
                          key={policy.id}
                          className="bg-white hover:bg-gray-100 text-center border-b border-gray-300 last:border-0"
                        >
                          <td className="px-6 py-6">{policy.policyNumber}</td>
                          <td className="px-6 py-6">
                            <BadgeIndicator
                              status={policy.status.toLowerCase()}
                              text={policy.status}
                            />
                          </td>
                          <td className="px-6 py-6">
                            {policy.applicantInfo.firstName}
                          </td>
                          <td className="px-6 py-6">
                            {policy.applicantInfo.lastName}
                          </td>
                          <td className="px-6 py-6">
                            {policy.applicantInfo.dateOfBirth}
                          </td>
                          <td className="px-6 py-6">
                            {policy.applicantInfo.email}
                          </td>
                          <td className="px-6 py-6">{policy.startDate}</td>
                          <td className="px-6 py-6">{policy.endDate}</td>
                          <td className="px-6 py-6">
                            <div className="flex justify-center items-center">
                              <button
                                className="text-white bg-ingle-light-purple hover:bg-ingle-dark-purple focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-2 hover:cursor-pointer"
                                onClick={() => {
                                  setIsModalOpen(true);
                                  setSelectedRecord({
                                    ...selectedRecord,
                                    email: policy.applicantInfo.email,
                                    policyId: policy.id,
                                  });
                                }}
                              >
                                <div className="inline-flex items-center">
                                  <Mail
                                    width={16}
                                    height={16}
                                    className="mr-2"
                                  />
                                  <span>Email</span>
                                </div>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={filterData.page}
                totalPages={data?.meta.totalPages}
                onPageChange={setCurrentPage}
              />

              <Modal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen}>
                <Email selectedRecord={selectedRecord} />
              </Modal>
            </>
          )}
        </>
      ) : (
        <>
          {!auth0IsLoading ? (
            <button
              onClick={() => loginWithRedirect()}
              className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 hover:cursor-pointer"
            >
              Log In
            </button>
          ) : (
            <Loader />
          )}
        </>
      )}
    </div>
  );
};

export default PoliciesPage;
